<template>
  <div class="group-card" :class="{ active: isSelected }" @click="$emit('select', group)">

    <!-- ================= HEADER ================= -->
    <div class="group-header">
      <div class="group-title">{{ group.name || 'NA' }}</div>

      <v-menu offset-y left :close-on-content-click="true">
        <template #activator="{ on, attrs }">
          <v-btn icon small v-bind="attrs" v-on="on" @click.stop>
            <v-icon small>mdi-dots-vertical</v-icon>
          </v-btn>
        </template>

        <div class="menu-card" @click.stop>
          <div class="menu-label">More</div>

          <div class="menu-item" @click="$emit('view-detail', group)">
            <span class="menu-text">View Detail</span>
            <span class="menu-arrow">→</span>
          </div>

          <div class="menu-item" @click="$emit('view-logs', group)">
            <span class="menu-text">View Logs</span>
            <v-icon small color="#11A8C9">mdi-clipboard-text-outline</v-icon>
          </div>

          <div class="menu-item" @click="$emit('edit', group)">
            <span class="menu-text">Edit Group</span>
            <v-icon small color="#4CAF50">mdi-pencil-outline</v-icon>
          </div>

          <div class="menu-item danger" @click="$emit('delete', group)">
            <span class="menu-text">Delete Group</span>
            <v-icon small color="#e53935">mdi-trash-can-outline</v-icon>
          </div>
        </div>
      </v-menu>
    </div>

    <!-- ================= AREA ================= -->
    <div class="group-area">
      <v-icon small class="mr-2" color="#11A8C9">mdi-map-marker-outline</v-icon>
      {{ areaName }}
    </div>

    <!-- ================= DATE ================= -->
    <div class="group-date">
      <v-icon small class="mr-2" color="#11A8C9">mdi-calendar-blank-outline</v-icon>
      {{ formattedDate }}
    </div>

    <!-- ================= BUSINESSES ================= -->
    <div class="mt-4">
      <div class="business-title">
        Businesses <span class="count">({{ restaurantCount }})</span>
      </div>

      <div class="business-list">
        <template v-if="restaurantCount">
          <div v-for="restaurant in group.restaurants" :key="restaurant.id" class="business-pill">
            <v-icon small class="mr-2" color="#1E3A8A">mdi-storefront-outline</v-icon>
            {{ restaurant.name || 'NA' }}
          </div>
        </template>
        <div v-else class="business-empty">NA</div>
      </div>
    </div>

    <!-- ================= DRIVER ================= -->
    <div class="group-driver mt-4">
      <v-icon small class="mr-2" color="#11A8C9">mdi-account-outline</v-icon>
      Driver Assigned: <strong class="ml-1">{{ driverName }}</strong>
    </div>

    <!-- ================= SHIFT ================= -->
    <div class="group-shift">
      <v-icon small class="mr-2" color="#11A8C9">mdi-clock-outline</v-icon>
      Shift: <strong class="ml-1">{{ shiftTime }}</strong>
    </div>

  </div>
</template>

<script>
export default {
  name: 'RestaurantGroupCard',

  props: {
    group: { type: Object, required: true },
    selectedIds: { type: Array, default: () => [] }
  },

  computed: {
    isSelected() {
      return this.selectedIds.includes(this.group.id)
    },
    areaName() {
      return this.group.area && this.group.area.name ? this.group.area.name : 'NA'
    },
    restaurantCount() {
      return this.group.restaurants ? this.group.restaurants.length : 0
    },
    driverName() {
      return this.group.driver && this.group.driver.name ? this.group.driver.name : 'NA'
    },
    shiftTime() {
      if (!this.group.shift_start || !this.group.shift_end) return 'NA'
      return this.group.shift_start + ' - ' + this.group.shift_end
    },
    formattedDate() {
      if (!this.group.created_at) return 'NA'
      try {
        return new Date(this.group.created_at).toLocaleDateString('en-GB', {
          day: '2-digit', month: 'long', year: 'numeric'
        })
      } catch (e) {
        return 'NA'
      }
    }
  }
}
</script>

<style scoped>
/* ================= CARD ================= */
.group-card {
  background: #ffffff;
  border-radius: 20px;
  padding: 22px;
  margin-bottom: 18px;
  border: 2px solid transparent;
  cursor: pointer;
  transition: all 0.25s ease;
  box-shadow: 0 4px 18px rgba(0, 0, 0, 0.03);
}

.group-card:hover {
  border-color: #dceef5;
}

.group-card.active {
  border-color: #11A8C9;
  box-shadow: 0 6px 24px rgba(17, 168, 201, 0.15);
}

/* ================= HEADER ================= */
.group-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 10px;
}

.group-title {
  font-weight: 700;
  font-size: 18px;
  color: #0b1b3f;
}

/* ================= AREA & DATE ================= */
.group-area,
.group-date {
  font-size: 14px;
  color: #6b7280;
  margin-bottom: 8px;
  display: flex;
  align-items: center;
}

/* ================= BUSINESSES ================= */
.business-title {
  font-weight: 600;
  font-size: 15px;
  margin-bottom: 10px;
}

.business-title .count {
  color: #11A8C9;
  font-weight: 700;
}

.business-list {
  display: flex;
  flex-wrap: wrap;
  gap: 10px;
}

.business-pill {
  background: #f1f3f6;
  padding: 8px 14px;
  border-radius: 24px;
  font-size: 13px;
  display: flex;
  align-items: center;
  font-weight: 500;
}

.business-empty {
  font-size: 13px;
  color: #9ca3af;
}

/* ================= DRIVER & SHIFT ================= */
.group-driver,
.group-shift {
  font-size: 14px;
  color: #4a5568;
  display: flex;
  align-items: center;
  margin-top: 8px;
}

/* ================= DROPDOWN MENU ================= */
.menu-card {
  background: #ffffff;
  border-radius: 20px;
  padding: 12px;
  width: 230px;
  box-shadow: 0 8px 40px rgba(0, 0, 0, 0.13);
}

.menu-label {
  font-size: 13px;
  color: #9ca3af;
  font-weight: 500;
  padding: 4px 12px 10px 12px;
}

.menu-item {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 13px 16px;
  border-radius: 12px;
  cursor: pointer;
  background: #f5f6f8;
  margin-bottom: 8px;
  transition: background 0.15s ease;
}

.menu-item:last-child {
  margin-bottom: 0;
}

.menu-item:hover {
  background: #eef0f3;
}

.menu-item.danger:hover {
  background: #fff0f0;
}

.menu-text {
  font-size: 15px;
  font-weight: 600;
  color: #0b1b3f;
}

.menu-item.danger .menu-text {
  color: #e53935;
}

.menu-arrow {
  font-size: 18px;
  font-weight: 700;
  color: #0b1b3f;
}
</style>